import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { toneFor } from '../constants/statTones';
import {
  colourForToken,
  enterAt,
  formatRatio,
  formatValue,
  toneForToken,
  WidgetPeriodChip,
} from '../shared';

/** The narrowest a card can get before its figure and bar stop being legible. */
const CARD_MIN_PX = 112;

/**
 * How full each resource is — `USAGE_MAP`.
 *
 * ## Why this is not the heat map
 *
 * It used to be: `usage_map` resolved to `HeatMapWidget` in its `calendar`
 * arrangement, because both are a grid of coloured cells. The cells are where
 * the resemblance ends. A heat map's cell is anonymous — its meaning is its
 * position in the matrix, and the colour is the whole message. A resource is
 * not at a position: it has a **name**, a **percentage**, and a quota it is
 * measured against, and all three have to be on the card. Squeezed into a
 * heat map cell they were a two-letter label over an unreadable number.
 *
 * ## Full against its own quota, not against the busiest
 *
 * A heat map shades against the peak in the data, which answers "which is
 * busiest". This answers a different question — "which is *running out*" —
 * and the two disagree exactly when it matters: eight bays all at 90% would
 * shade as a flat grid under peak-relative colour, and every one of them is
 * about to be a problem. `utilization_pct` is each resource's own ratio and
 * that is what the bar and the colour follow.
 *
 * `color_token` is the backend's verdict on that ratio, against the author's
 * thresholds. It drives the bar **and** a corner mark: a reader scanning
 * eight cards for trouble should not have to judge shades of amber.
 *
 * ## Without a quota it still says how much
 *
 * `limit` is optional in the contract and the builder leaves it empty unless
 * the author fills it, so a usage map built on a plain count arrives as
 * `{key, label, value}` and nothing else. The card used to print a dash for
 * the figure and hide the `value / limit` line for want of a limit — twenty
 * cards, twenty dashes, over data that was all there (measured on dev
 * 2026-10-26 on `[23-oct] Uso por función · UI`). A cell with no ratio shows
 * its **value** instead, and drops the bar: a percentage is what the bar
 * measures, and an empty rail under a raw count reads as nought percent.
 *
 * @param {object} props
 * @param {object} props.data `USAGE_MAP`'s `cells[]`.
 * @param {string} [props.className]
 */
function UsageMapWidget({ data, className = '' }) {
  const format = data?.format ?? null;

  const cells = useMemo(() => (data?.cells ?? []).map((cell, index) => {
    const ratio = typeof cell.utilization_pct === 'number'
      ? cell.utilization_pct
      : (cell.limit > 0 ? (cell.value ?? 0) / cell.limit : null);

    return {
      key: cell.key ?? cell.label ?? String(index),
      label: cell.label ?? cell.key ?? '',
      group: cell.group ?? '',
      value: cell.value,
      limit: cell.limit,
      ratio,
      tone: toneForToken(cell.color_token),
      colour: colourForToken(cell.color_token),
      /** Only a configured threshold earns a mark; a shade is not an alert. */
      marked: Boolean(cell.color_token) && cell.color_token !== 'NEUTRAL',
    };
  }), [data]);

  if (!cells.length) return null;

  return (
    <div className={`flex min-w-0 flex-col gap-2 ${className}`.trim()}>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN_PX}px, 1fr))` }}
      >
        {cells.map((cell, index) => {
          const enter = enterAt(index, 'scale');
          const tone = toneFor(cell.tone);
          const Mark = tone.defaultIcon;

          return (
            <div
              key={cell.key}
              className={`flex min-w-0 flex-col gap-1 rounded-lg border border-edge bg-surface p-2.5 ${enter.className}`}
              style={enter.style}
            >
              <div className="flex min-w-0 items-start justify-between gap-1">
                <span className="min-w-0 truncate text-[10px] text-content-muted">
                  {cell.label}
                </span>
                {cell.marked ? (
                  <Mark size={12} className="shrink-0" style={{ color: cell.colour }} aria-hidden />
                ) : null}
              </div>

              <span className="text-lg font-bold leading-none text-content">
                {cell.ratio === null
                  ? formatValue(cell.value, format)
                  : formatRatio(cell.ratio, { signed: false, decimals: 0 })}
              </span>

              {/* The bar measures a ratio. With no quota there is none to
                  measure, and an empty rail under a raw count reads as 0%. */}
              {cell.ratio === null ? null : (
                <span className="block h-1 w-full rounded-full bg-[rgb(var(--wg-wash))]">
                  <span
                    className="block h-1 rounded-full"
                    style={{
                      width: `${Math.min(100, Math.max(0, cell.ratio * 100))}%`,
                      background: cell.colour,
                    }}
                  />
                </span>
              )}

              {/* How much of how much — a percentage with no quota behind it
                  cannot be acted on. */}
              {cell.limit != null ? (
                <span className="truncate text-[10px] text-content-faint">
                  {`${formatValue(cell.value, format, '—')} / ${formatValue(cell.limit, format)}`}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      <WidgetPeriodChip period={data?.period} className="self-start" />
    </div>
  );
}

UsageMapWidget.propTypes = {
  data: PropTypes.shape({
    cells: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      group: PropTypes.string,
      value: PropTypes.number,
      limit: PropTypes.number,
      utilization_pct: PropTypes.number,
      color_token: PropTypes.string,
    })).isRequired,
    format: PropTypes.object,
    period: PropTypes.object,
  }).isRequired,
  className: PropTypes.string,
};

export default memo(UsageMapWidget);
